<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>
    
    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">📊</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>
    
    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        >
          <template slot-scope="{ row }" slot="dataTime">
            {{ formatTime(row.dataTime) }}
          </template>
          <template slot-scope="{ row }" slot="deviceName">
            <div class="device-info">
              <div class="device-name-primary">{{ row.deviceName || row.deviceId }}</div>
              <div class="device-name-localized">
                {{ lang === 'zh' ? row.deviceNameCn : row.deviceNameEn }}
              </div>
            </div>
          </template>
          <template slot-scope="{ row }" slot="paramName">
            <div class="param-info">
              <div class="param-name-primary">{{ row.paramName }}</div>
              <div class="param-name-localized">
                {{ lang === 'zh' ? row.paramNameCn : row.paramNameEn }}
              </div>
            </div>
          </template>
          <template slot-scope="{ row }" slot="data">
            <span class="data-value">{{ row.data }}{{ row.paramUnit ? ' ' + row.paramUnit : '' }}</span>
          </template>
        </Table>
      </div>
      
      <!-- 自定义分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <!-- 总数信息 -->
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>
          
          <!-- 页码按钮组 -->
          <div class="pagination-controls">
            <!-- 首页 -->
            <button 
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>
            
            <!-- 上一页 -->
            <button 
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>
            
            <!-- 页码按钮 -->
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>
            
            <!-- 下一页 -->
            <button 
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>
            
            <!-- 尾页 -->
            <button 
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>
          
          <!-- 每页条数选择 -->
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>
          
          <!-- 快速跳转 -->
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input 
              type="number" 
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';

export default {
  name: 'DeviceStatusTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap: langMap,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      windowHeight: window.innerHeight,
      columns: [
        {
          title: langMap.dashboard_dataTime[this.$store.state.app.language],
          slot: 'dataTime',
          width: 180,
          align: 'center'
        },
        {
          title: langMap.dashboard_deviceId[this.$store.state.app.language],
          key: 'deviceId',
          width: 100,
          align: 'center'
        },
        {
          title: langMap.dashboard_deviceName[this.$store.state.app.language],
          slot: 'deviceName',
          width: 200,
          align: 'center'
        },
        {
          title: langMap.dashboard_paramName[this.$store.state.app.language],
          slot: 'paramName',
          width: 200,
          align: 'center'
        },
        {
          title: langMap.dashboard_paramValue[this.$store.state.app.language],
          slot: 'data',
          minWidth: 150,
          align: 'center'
        },
        {
          title: langMap.dashboard_robotCode[this.$store.state.app.language],
          key: 'robotCode',
          width: 150,
          align: 'center'
        }
      ]
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    // 动态计算表格高度
    tableHeight() {
      return Math.max(400, this.windowHeight - 502);
    },
    // 总页数
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1;
    },
    // 当前页范围文本
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1;
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total);
      return `${start}-${end}`;
    },
    // 可见页码
    visiblePages() {
      const current = this.pagination.current;
      const total = this.totalPages;
      const pages = [];
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        }
      }
      
      return pages;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    // 更新 columns 标题语言
    this.updateColumnsTitle();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    lang() {
      this.updateColumnsTitle();
    }
  },
  methods: {
    updateColumnsTitle() {
      this.columns[0].title = this.langMap.dashboard_dataTime[this.lang];
      this.columns[1].title = this.langMap.dashboard_deviceId[this.lang];
      this.columns[2].title = this.langMap.dashboard_deviceName[this.lang];
      this.columns[3].title = this.langMap.dashboard_paramName[this.lang];
      this.columns[4].title = this.langMap.dashboard_paramValue[this.lang];
      this.columns[5].title = this.langMap.dashboard_robotCode[this.lang];
    },
    formatTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    handlePageChange(page) {
      this.$emit('page-change', page);
    },
    handlePageSizeChange(size) {
      this.$emit('size-change', size);
    },
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(this.langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages));
        return;
      }
      this.handlePageChange(this.jumpPage);
      this.jumpPage = null;
    },
    handleResize() {
      this.windowHeight = window.innerHeight;
    }
  }
}
</script>

<style lang="less" scoped>
.data-display-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>